/**
 * A glTF's external buffers and images, read through the resource provider and packed
 * with its JSON into one GLB, so the glTF loader fetches nothing. The VS Code webview's
 * CSP refuses every fetch, a provider, not a URL, owns a `res://` file, and a URI in a
 * crafted file must not make the viewer request it. THREE-free, so it runs and tests
 * without a renderer.
 */
import { normalizeRelativePath, RES_SCHEME } from '../../resPath';
import { SVG_MIME_TYPE, imageMimeType } from '../image/extensions';
import { GLB_BIN_CHUNK, GLB_JSON_CHUNK, GLB_MAGIC, dataUriMimeType, isDataUri, } from '../../../godot/import/gltf';
import { getBaseDir, pathJoin } from '../../../godot/string/string';
import { decodeBase64Bytes } from '../../../godot/variant/packedBytes';
/** The container version Godot writes and the glTF 2.0 spec requires. */
const GLB_VERSION = 2;
/** The glTF spec aligns every GLB chunk, and this packer every resource, to four bytes. */
const ALIGNMENT = 4;
/** The GLB file header: magic, version and total length, each a little-endian `uint32`. */
const GLB_HEADER_BYTES = 12;
const GLB_TOTAL_LENGTH_OFFSET = 8;
/** A GLB chunk header: the chunk's length, then its type. */
const GLB_CHUNK_HEADER_BYTES = 8;
/** Whether `data` is a binary glTF container, as against a text `.gltf`. */
function isGlbContainer(data) {
    return data.byteLength >= 4 && new DataView(data).getUint32(0, true) === GLB_MAGIC;
}
/**
 * The `res://` path a glTF URI names: percent-decoded, relative to the glTF's own directory.
 * Null for a URI that climbs above `res://`, which names no project file.
 */
export function gltfResourcePath(gltfPath, uri) {
    const scheme = gltfPath.startsWith(RES_SCHEME) ? RES_SCHEME : '';
    const directory = getBaseDir(gltfPath.slice(scheme.length));
    // `p_base_path.path_join(uri)` (`gltf_document.cpp:792`, `:2352`).
    const relative = normalizeRelativePath(pathJoin(directory, percentDecoded(uri)));
    return relative === null ? null : scheme + relative;
}
/** `uri` percent-decoded, or as written when an escape is malformed. */
function percentDecoded(uri) {
    try {
        return decodeURIComponent(uri);
    }
    catch {
        return uri;
    }
}
const padded = (length) => Math.ceil(length / ALIGNMENT) * ALIGNMENT;
/** A data URI's bytes, base64 or percent-encoded. */
function dataUriBytes(uri) {
    const comma = uri.indexOf(',');
    const payload = uri.slice(comma + 1);
    if (uri.slice(0, comma).endsWith(';base64'))
        return decodeBase64Bytes(payload);
    return new TextEncoder().encode(decodeURIComponent(payload));
}
/** Collects resources into one binary chunk, each at a four-byte-aligned offset. */
class BinaryChunk {
    parts = [];
    length = 0;
    append(bytes) {
        const offset = this.length;
        this.parts.push({ offset, bytes });
        this.length = padded(offset + bytes.byteLength);
        return offset;
    }
    bytes() {
        const out = new Uint8Array(this.length);
        for (const { offset, bytes } of this.parts)
            out.set(bytes, offset);
        return out;
    }
}
/**
 * The JSON and binary chunk three's glTF loader reads: it walks every chunk to the header's
 * total length and keeps the last of each type (`GLTFBinaryExtension` in `GLTFLoader.js`).
 * This walks the same way, so both read one document. Throws where the loader throws.
 */
function glbChunks(glb) {
    const contentsLength = new DataView(glb).getUint32(GLB_TOTAL_LENGTH_OFFSET, true) - GLB_HEADER_BYTES;
    const chunks = new DataView(glb, GLB_HEADER_BYTES);
    let text = null;
    let bin = new Uint8Array();
    for (let index = 0; index < contentsLength;) {
        const length = chunks.getUint32(index, true);
        const type = chunks.getUint32(index + 4, true);
        const body = new Uint8Array(glb, GLB_HEADER_BYTES + index + GLB_CHUNK_HEADER_BYTES, length);
        if (type === GLB_JSON_CHUNK)
            text = new TextDecoder().decode(body);
        else if (type === GLB_BIN_CHUNK)
            bin = body;
        index += GLB_CHUNK_HEADER_BYTES + length;
    }
    if (text === null)
        throw new Error('GLB has no JSON chunk');
    return { json: JSON.parse(text), bin };
}
/** A text glTF's JSON document, with the empty binary chunk it has no container for. */
function textGltfDocument(data) {
    return { json: JSON.parse(new TextDecoder().decode(data)), bin: new Uint8Array() };
}
/** Whether a buffer or an image names its bytes by `uri`, which the glTF loader would fetch. */
function namesUri(json) {
    return [...(json.buffers ?? []), ...(json.images ?? [])].some((entry) => entry.uri !== undefined);
}
/**
 * `data` as a GLB that carries every buffer and image it names: a text glTF packed, a GLB
 * that names a `uri` repacked, and any other GLB as it is. The glTF loader then fetches nothing.
 */
export async function selfContainedGlb(data, readUri) {
    const isGlb = isGlbContainer(data);
    const { json, bin } = isGlb ? glbChunks(data) : textGltfDocument(data);
    const images = json.images ?? [];
    if (isGlb && !namesUri(json))
        return { glb: data, images };
    return { glb: await packGltfAsGlb(json, readUri, bin), images };
}
/**
 * `json` as a GLB: every buffer and every image with a `uri` moves into the one
 * binary chunk, and the buffer views point into it. `glbBin` is the binary chunk of the
 * GLB that `json` came from, which the first buffer names when it has no `uri`. The
 * input is not changed.
 */
async function packGltfAsGlb(json, readUri, glbBin = new Uint8Array()) {
    const read = async (uri) => isDataUri(uri) ? dataUriBytes(uri) : new Uint8Array(await readUri(uri));
    const chunk = new BinaryChunk();
    const bufferOffsets = [];
    for (const [index, buffer] of (json.buffers ?? []).entries()) {
        const ownBytes = index === 0 ? glbBin : new Uint8Array();
        bufferOffsets.push(chunk.append(buffer.uri === undefined ? ownBytes : await read(buffer.uri)));
    }
    const bufferViews = (json.bufferViews ?? []).map((view) => ({
        ...view,
        buffer: 0,
        byteOffset: bufferOffsets[view.buffer] + (view.byteOffset ?? 0),
    }));
    const images = [];
    for (const image of json.images ?? []) {
        if (image.uri === undefined) {
            images.push(image);
            continue;
        }
        const { uri, ...rest } = image;
        const bytes = await read(uri);
        bufferViews.push({ buffer: 0, byteOffset: chunk.append(bytes), byteLength: bytes.byteLength });
        const mimeType = rest.mimeType ?? dataUriMimeType(uri) ?? uriImageMimeType(uri);
        images.push({ ...rest, bufferView: bufferViews.length - 1, ...(mimeType && { mimeType }) });
    }
    const packed = { ...json, buffers: [{ byteLength: chunk.length }], bufferViews };
    if (json.images)
        packed.images = images;
    return glbOf(packed, chunk.bytes());
}
/**
 * The MIME type of an image URI by its extension, its query left off. An SVG gets none: Godot's
 * glTF importer loads no SVG (`gltf_document.cpp:2136-2177`), and the browser decodes an SVG only
 * under its MIME type.
 */
function uriImageMimeType(uri) {
    const mimeType = imageMimeType(uri.split('?')[0]);
    return mimeType === SVG_MIME_TYPE ? undefined : mimeType;
}
/** The GLB container: a 12-byte header, the JSON chunk padded with spaces, the binary chunk. */
function glbOf(json, bin) {
    const text = new TextEncoder().encode(JSON.stringify(json));
    const jsonLength = padded(text.byteLength);
    const total = 12 + 8 + jsonLength + 8 + bin.byteLength;
    const out = new Uint8Array(total);
    const view = new DataView(out.buffer);
    view.setUint32(0, GLB_MAGIC, true);
    view.setUint32(4, GLB_VERSION, true);
    view.setUint32(8, total, true);
    view.setUint32(12, jsonLength, true);
    view.setUint32(16, GLB_JSON_CHUNK, true);
    out.fill(0x20, 20, 20 + jsonLength);
    out.set(text, 20);
    const binStart = 20 + jsonLength;
    view.setUint32(binStart, bin.byteLength, true);
    view.setUint32(binStart + 4, GLB_BIN_CHUNK, true);
    out.set(bin, binStart + 8);
    return out.buffer;
}
//# sourceMappingURL=gltfResources.js.map